import {
    CSRF_COOKIE_NAME,
    SESSION_COOKIE_NAME,
    PRESENCE_ACTIVE_WINDOW_SECONDS,
    type ISessionPresenceStats
} from "@ids/shared";
import { fetchWithError } from "../../utils/fetchWithError";
import type {
    IAddApiKeyDto,
    IAdminInvitationQuery,
    ICreateClientDto,
    ICreateInvitationAdminDto,
    ICreateMessageAdminDto,
    ICreateRole,
    ICreateUserDto,
    IInvitation,
    IMessage,
    IUpdateClientDto,
    IUpdateUserDto,
    IAdminUserQuery,
    IClient,
    IRole,
    IUser,
    PopulatedUser,
    IProfile,
    IAccount,
    PopulatedAccount,
    PopulatedAppUser,
    IApiKey,
    DeleteApiKeyResult,
    StatusResponse
} from "@tintoa/identity-service-client";
import { InvitationStatus } from "@tintoa/identity-service-client";

export interface CleanupResult {
    appUsers: {
        count: number;
        deleted: number;
        items: Array<{
            id: string;
            tid: string;
            clientId: string;
            reason: string;
        }>;
    };
    accounts: {
        count: number;
        deleted: number;
        items: Array<{ id: string; tid: string; reason: string }>;
    };
    clients: {
        count: number;
        deleted: number;
        items: Array<{ id: string; name: string; reason: string }>;
    };
    invitations: {
        count: number;
        deleted: number;
        items: Array<{
            id: string;
            email: string;
            clientId: string;
            reason: string;
        }>;
    };
    securityInformation: {
        count: number;
        deleted: number;
        items: Array<{ id: string; tid: string; reason: string }>;
    };
    summary: {
        totalOrphanedRecords: number;
        totalDeletedRecords: number;
        dryRun: boolean;
    };
}

export interface EmailTestResponse {
    success: boolean;
    message: string;
}

export interface ListResult<T> {
    data: T[];
    total: number;
}

/**
 * Internal Admin Client for frontend use only.
 * Uses direct fetch calls to admin endpoints.
 */
export class InternalAdminClient {
    private baseUrl: string;

    constructor(baseUrl: string = window.location.origin) {
        this.baseUrl = baseUrl;
    }

    private getAuthHeaders(): HeadersInit {
        const token = this.getCookieValue(SESSION_COOKIE_NAME);
        const csrf = this.getCookieValue(CSRF_COOKIE_NAME);
        const headers: HeadersInit = {
            "Content-Type": "application/json"
        };

        if (token) {
            headers["Authorization"] = `Bearer ${token}`;
        }
        if (csrf) {
            headers["X-CSRF-Token"] = csrf;
        }

        return headers;
    }

    private getCookieValue(name: string): string | null {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) {
            return parts.pop()?.split(";").shift() || null;
        }
        return null;
    }

    // Client Management
    async getClients(): Promise<IClient[]> {
        return fetchWithError<IClient[]>(`${this.baseUrl}/admin/client`, {
            method: "GET",
            credentials: "include",
            headers: this.getAuthHeaders()
        });
    }

    async createClient(clientData: ICreateClientDto): Promise<IClient> {
        return fetchWithError<IClient>(`${this.baseUrl}/admin/client`, {
            method: "POST",
            credentials: "include",
            headers: this.getAuthHeaders(),
            body: JSON.stringify(clientData)
        });
    }

    async updateClient(
        id: string,
        clientData: IUpdateClientDto
    ): Promise<IClient> {
        return fetchWithError<IClient>(`${this.baseUrl}/admin/client/${id}`, {
            method: "PUT",
            credentials: "include",
            headers: this.getAuthHeaders(),
            body: JSON.stringify(clientData)
        });
    }

    async deleteClient(id: string): Promise<IClient> {
        return fetchWithError<IClient>(`${this.baseUrl}/admin/client/${id}`, {
            method: "DELETE",
            credentials: "include",
            headers: this.getAuthHeaders()
        });
    }

    async uploadClientIcon(clientId: string, file: File): Promise<IClient> {
        const formData = new FormData();
        formData.append("icon", file);

        const token = this.getCookieValue(SESSION_COOKIE_NAME);
        const headers: HeadersInit = {};
        if (token) {
            headers["Authorization"] = `Bearer ${token}`;
        }

        return fetchWithError<IClient>(
            `${this.baseUrl}/admin/client/${clientId}/icon`,
            {
                method: "POST",
                credentials: "include",
                headers,
                body: formData
            }
        );
    }

    async deleteClientIcon(clientId: string): Promise<void> {
        return fetchWithError<void>(
            `${this.baseUrl}/admin/client/${clientId}/icon`,
            {
                method: "DELETE",
                credentials: "include",
                headers: this.getAuthHeaders()
            }
        );
    }

    async getClientById(id: string): Promise<IClient> {
        return fetchWithError<IClient>(`${this.baseUrl}/admin/client/${id}`, {
            method: "GET",
            credentials: "include",
            headers: this.getAuthHeaders()
        });
    }

    async addApiKey(
        clientId: string,
        apiKeyData: IAddApiKeyDto
    ): Promise<IApiKey> {
        return fetchWithError<IApiKey>(
            `${this.baseUrl}/admin/client/${clientId}/api-key`,
            {
                method: "POST",
                credentials: "include",
                headers: this.getAuthHeaders(),
                body: JSON.stringify(apiKeyData)
            }
        );
    }

    async removeApiKey(
        clientId: string,
        apiKeyName: string
    ): Promise<DeleteApiKeyResult> {
        return fetchWithError<DeleteApiKeyResult>(
            `${this.baseUrl}/admin/client/${clientId}/api-key/${apiKeyName}`,
            {
                method: "DELETE",
                credentials: "include",
                headers: this.getAuthHeaders()
            }
        );
    }

    async createClientRole(
        clientId: string,
        roleData: ICreateRole
    ): Promise<IRole> {
        return fetchWithError<IRole>(
            `${this.baseUrl}/admin/client/${clientId}/roles`,
            {
                method: "POST",
                credentials: "include",
                headers: this.getAuthHeaders(),
                body: JSON.stringify(roleData)
            }
        );
    }

    async getClientRoles(clientId: string): Promise<IRole[]> {
        return fetchWithError<IRole[]>(
            `${this.baseUrl}/admin/client/${clientId}/roles`,
            {
                method: "GET",
                credentials: "include",
                headers: this.getAuthHeaders()
            }
        );
    }

    async deleteClientRole(clientId: string, roleIdent: string): Promise<void> {
        return fetchWithError<void>(
            `${this.baseUrl}/admin/client/${clientId}/roles/${roleIdent}`,
            {
                method: "DELETE",
                credentials: "include",
                headers: this.getAuthHeaders()
            }
        );
    }

    // Role Management
    async getRoles(): Promise<IRole[]> {
        return fetchWithError<IRole[]>(`${this.baseUrl}/admin/roles`, {
            method: "GET",
            credentials: "include",
            headers: this.getAuthHeaders()
        });
    }

    async createRole(roleData: ICreateRole): Promise<IRole> {
        return fetchWithError<IRole>(`${this.baseUrl}/admin/roles`, {
            method: "POST",
            credentials: "include",
            headers: this.getAuthHeaders(),
            body: JSON.stringify(roleData)
        });
    }

    async getRole(ident: string): Promise<IRole> {
        return fetchWithError<IRole>(`${this.baseUrl}/admin/roles/${ident}`, {
            method: "GET",
            credentials: "include",
            headers: this.getAuthHeaders()
        });
    }

    async deleteRole(ident: string): Promise<void> {
        return fetchWithError<void>(`${this.baseUrl}/admin/roles/${ident}`, {
            method: "DELETE",
            credentials: "include",
            headers: this.getAuthHeaders()
        });
    }

    // User Management
    async getUsers(
        query: Partial<IAdminUserQuery> = {}
    ): Promise<ListResult<PopulatedUser>> {
        const params = new URLSearchParams();
        for (const [key, value] of Object.entries(query)) {
            if (value != null) {
                params.append(key, String(value));
            }
        }
        const queryString = params.toString();
        const url = queryString
            ? `${this.baseUrl}/admin/users?${queryString}`
            : `${this.baseUrl}/admin/users`;

        return fetchWithError<ListResult<PopulatedUser>>(url, {
            method: "GET",
            credentials: "include",
            headers: this.getAuthHeaders()
        });
    }

    async getUserDetails(tid: string): Promise<PopulatedUser> {
        return fetchWithError<PopulatedUser>(
            `${this.baseUrl}/admin/users/${tid}`,
            {
                method: "GET",
                credentials: "include",
                headers: this.getAuthHeaders()
            }
        );
    }

    async createUser(userData: ICreateUserDto) {
        return fetchWithError(`${this.baseUrl}/admin/users`, {
            method: "POST",
            credentials: "include",
            headers: this.getAuthHeaders(),
            body: JSON.stringify(userData)
        });
    }

    async updateUserProfile(
        tid: string,
        userData: IUpdateUserDto
    ): Promise<IProfile> {
        return fetchWithError<IProfile>(
            `${this.baseUrl}/admin/users/${tid}/profile`,
            {
                method: "PUT",
                credentials: "include",
                headers: this.getAuthHeaders(),
                body: JSON.stringify(userData)
            }
        );
    }

    async verifyUser(tid: string): Promise<IAccount> {
        return fetchWithError<IAccount>(
            `${this.baseUrl}/admin/users/${tid}/verify`,
            {
                method: "PUT",
                credentials: "include",
                headers: this.getAuthHeaders()
            }
        );
    }

    async lockUser(tid: string): Promise<IAccount> {
        return fetchWithError<IAccount>(
            `${this.baseUrl}/admin/users/${tid}/lock`,
            {
                method: "PUT",
                credentials: "include",
                headers: this.getAuthHeaders()
            }
        );
    }

    async unlockUser(tid: string): Promise<IAccount> {
        return fetchWithError<IAccount>(
            `${this.baseUrl}/admin/users/${tid}/unlock`,
            {
                method: "PUT",
                credentials: "include",
                headers: this.getAuthHeaders()
            }
        );
    }

    async addUserRole(tid: string, role: string): Promise<PopulatedAccount> {
        return fetchWithError<PopulatedAccount>(
            `${this.baseUrl}/admin/users/${tid}/roles/add`,
            {
                method: "PUT",
                credentials: "include",
                headers: this.getAuthHeaders(),
                body: JSON.stringify({ role })
            }
        );
    }

    async removeUserRole(tid: string, role: string): Promise<PopulatedAccount> {
        return fetchWithError<PopulatedAccount>(
            `${this.baseUrl}/admin/users/${tid}/roles/remove`,
            {
                method: "PUT",
                credentials: "include",
                headers: this.getAuthHeaders(),
                body: JSON.stringify({ role })
            }
        );
    }

    async deleteUser(tid: string): Promise<IUser> {
        return fetchWithError<IUser>(`${this.baseUrl}/admin/users/${tid}`, {
            method: "DELETE",
            credentials: "include",
            headers: this.getAuthHeaders()
        });
    }

    async privilegeUser(tid: string): Promise<PopulatedAccount> {
        return fetchWithError<PopulatedAccount>(
            `${this.baseUrl}/admin/users/${tid}/privilege`,
            {
                method: "PUT",
                credentials: "include",
                headers: this.getAuthHeaders()
            }
        );
    }

    async degradeUser(tid: string): Promise<PopulatedAccount> {
        return fetchWithError<PopulatedAccount>(
            `${this.baseUrl}/admin/users/${tid}/degrade`,
            {
                method: "PUT",
                credentials: "include",
                headers: this.getAuthHeaders()
            }
        );
    }

    async getUserAppUsers(tid: string): Promise<PopulatedAppUser[]> {
        return fetchWithError<PopulatedAppUser[]>(
            `${this.baseUrl}/admin/users/${tid}/app-users`,
            {
                method: "GET",
                credentials: "include",
                headers: this.getAuthHeaders()
            }
        );
    }

    async getAppUser(
        tid: string,
        appUserId: string
    ): Promise<PopulatedAppUser> {
        return fetchWithError<PopulatedAppUser>(
            `${this.baseUrl}/admin/users/${tid}/app-users/${appUserId}`,
            {
                method: "GET",
                credentials: "include",
                headers: this.getAuthHeaders()
            }
        );
    }

    async getClientAppUsers(
        clientId: string,
        params?: { limit?: number; offset?: number }
    ): Promise<{ total: number; data: PopulatedAppUser[] }> {
        const searchParams = new URLSearchParams();
        if (params?.limit != null)
            searchParams.set("limit", String(params.limit));
        if (params?.offset != null)
            searchParams.set("offset", String(params.offset));
        const query = searchParams.toString();
        const url = `${this.baseUrl}/admin/client/${clientId}/app-users${
            query ? `?${query}` : ""
        }`;
        return fetchWithError<{ total: number; data: PopulatedAppUser[] }>(
            url,
            {
                method: "GET",
                credentials: "include",
                headers: this.getAuthHeaders()
            }
        );
    }

    async updateAppUser(
        tid: string,
        appUserId: string,
        data: {
            roles?: string[];
            scopes?: string[];
            meta?: Record<string, string>;
        }
    ): Promise<PopulatedAppUser> {
        return fetchWithError<PopulatedAppUser>(
            `${this.baseUrl}/admin/users/${tid}/app-users/${appUserId}`,
            {
                method: "PUT",
                credentials: "include",
                headers: this.getAuthHeaders(),
                body: JSON.stringify(data)
            }
        );
    }

    async deleteAppUser(tid: string, appUserId: string): Promise<void> {
        return fetchWithError<void>(
            `${this.baseUrl}/admin/users/${tid}/app-users/${appUserId}`,
            {
                method: "DELETE",
                credentials: "include",
                headers: this.getAuthHeaders()
            }
        );
    }

    // Invitation Management
    async getInvitations(
        query: Partial<IAdminInvitationQuery> = {}
    ): Promise<ListResult<IInvitation>> {
        const params = new URLSearchParams();
        for (const [key, value] of Object.entries(query)) {
            if (value != null) {
                params.append(key, String(value));
            }
        }
        const queryString = params.toString();
        const url = queryString
            ? `${this.baseUrl}/admin/invitations?${queryString}`
            : `${this.baseUrl}/admin/invitations`;

        return fetchWithError<ListResult<IInvitation>>(url, {
            method: "GET",
            credentials: "include",
            headers: this.getAuthHeaders()
        });
    }

    async createInvitation(
        invitationData: ICreateInvitationAdminDto
    ): Promise<IInvitation> {
        return fetchWithError<IInvitation>(
            `${this.baseUrl}/admin/invitations`,
            {
                method: "POST",
                credentials: "include",
                headers: this.getAuthHeaders(),
                body: JSON.stringify(invitationData)
            }
        );
    }

    async deleteInvitation(id: string): Promise<{ deleted: boolean }> {
        return fetchWithError<{ deleted: boolean }>(
            `${this.baseUrl}/admin/invitations/${id}`,
            {
                method: "DELETE",
                credentials: "include",
                headers: this.getAuthHeaders()
            }
        );
    }

    async createMessage(data: ICreateMessageAdminDto): Promise<IMessage> {
        return fetchWithError<IMessage>(`${this.baseUrl}/admin/messages`, {
            method: "POST",
            credentials: "include",
            headers: this.getAuthHeaders(),
            body: JSON.stringify(data)
        });
    }

    async getRoleMessages(
        query: {
            read?: boolean;
            limit?: number;
            offset?: number;
            recipientRole?: string;
        } = {}
    ): Promise<ListResult<IMessage>> {
        const params = new URLSearchParams();
        if (query.read !== undefined) params.set("read", String(query.read));
        if (query.limit !== undefined) params.set("limit", String(query.limit));
        if (query.offset !== undefined)
            params.set("offset", String(query.offset));
        if (query.recipientRole)
            params.set("recipientRole", query.recipientRole);
        const qs = params.toString();
        return fetchWithError<ListResult<IMessage>>(
            `${this.baseUrl}/admin/role-messages${qs ? `?${qs}` : ""}`,
            {
                method: "GET",
                credentials: "include",
                headers: this.getAuthHeaders()
            }
        );
    }

    async markRoleMessageRead(id: string): Promise<IMessage> {
        return fetchWithError<IMessage>(
            `${this.baseUrl}/admin/role-messages/${id}/read`,
            {
                method: "PUT",
                credentials: "include",
                headers: this.getAuthHeaders()
            }
        );
    }

    async markAllRoleMessagesRead(): Promise<{ modified: number }> {
        return fetchWithError<{ modified: number }>(
            `${this.baseUrl}/admin/role-messages/read-all`,
            {
                method: "PUT",
                credentials: "include",
                headers: this.getAuthHeaders()
            }
        );
    }

    // Data Cleanup
    async performDataCleanup(dryRun: boolean = true): Promise<CleanupResult> {
        return fetchWithError<CleanupResult>(
            `${this.baseUrl}/admin/data-cleanup?dryRun=${dryRun}`,
            {
                method: "POST",
                credentials: "include",
                headers: this.getAuthHeaders()
            }
        );
    }

    // Health Check
    async getSystemHealth(): Promise<StatusResponse> {
        // Health endpoint is public and doesn't require authentication.
        // On Redis (or other) failure the backend returns 503 with the same JSON shape (status, details).
        // We always parse the body so the frontend can show e.g. "Redis Offline" even when status is error.
        try {
            const response = await fetch(`${this.baseUrl}/health`, {
                method: "GET",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json"
                }
            });

            const data = await response.json();

            if (!response.ok) {
                if (data && typeof data === "object" && "details" in data) {
                    return data as StatusResponse;
                }
                throw new Error(
                    `Health check failed: ${response.status} ${response.statusText}`
                );
            }

            return data as StatusResponse;
        } catch (error) {
            throw error;
        }
    }

    // Email Test
    async testEmail(
        email: string,
        emailType: "verification" | "reset-password" | "invitation"
    ): Promise<EmailTestResponse> {
        return fetchWithError<EmailTestResponse>(
            `${this.baseUrl}/admin/email-test`,
            {
                method: "POST",
                credentials: "include",
                headers: this.getAuthHeaders(),
                body: JSON.stringify({ email, emailType })
            }
        );
    }

    async getSessionPresenceStats(): Promise<ISessionPresenceStats> {
        return fetchWithError<ISessionPresenceStats>(
            `${this.baseUrl}/admin/sessions/stats`,
            {
                method: "GET",
                credentials: "include",
                headers: this.getAuthHeaders()
            }
        );
    }

    // Dashboard Stats
    async getDashboardStats() {
        const emptyPresence: ISessionPresenceStats = {
            uniqueLoggedInUsers: 0,
            activeUsers: 0,
            sessionCount: 0,
            activeWindowSeconds: PRESENCE_ACTIVE_WINDOW_SECONDS
        };
        const [clients, users, roles, invitations, healthStatus, presence] =
            await Promise.all([
                this.getClients(),
                this.getUsers(),
                this.getRoles(),
                this.getInvitations(),
                this.getSystemHealth(),
                this.getSessionPresenceStats().catch(() => emptyPresence)
            ]);

        // Determine system health based on actual health check
        let systemHealth: "healthy" | "warning" | "error" = "healthy";

        if (healthStatus && typeof healthStatus === "object") {
            const health = healthStatus as any;
            if (health.status === "error") {
                systemHealth = "error";
            } else if (health.status === "ok") {
                // Check individual components for warnings
                const hasWarnings =
                    health.details &&
                    Object.values(health.details).some((component: any) => {
                        if (component && typeof component === "object") {
                            return (
                                component.status === "down" ||
                                component.status === "error"
                            );
                        }
                        return false;
                    });
                systemHealth = hasWarnings ? "warning" : "healthy";
            } else {
                systemHealth = "warning";
            }
        } else {
            systemHealth = "error";
        }

        return {
            totalClients: clients.length,
            totalUsers: users.total,
            totalRoles: roles.length,
            totalApiKeys: clients.reduce(
                (total: number, client: any) =>
                    total + (client.apiKeys?.length || 0),
                0
            ),
            pendingInvitations: invitations.data.filter(
                (inv: IInvitation) => inv.status === InvitationStatus.Pending
            ).length,
            uniqueLoggedInUsers: presence.uniqueLoggedInUsers,
            activeUsers: presence.activeUsers,
            sessionCount: presence.sessionCount,
            activeWindowSeconds: presence.activeWindowSeconds,
            systemHealth,
            healthDetails: healthStatus
        };
    }
}
